/* eslint-disable */
import { Document, Packer, Paragraph, TextRun, HeadingLevel, AlignmentType, UnderlineType, BorderStyle } from 'docx';

/**
 * Converts markdown text to Microsoft Word document
 * @param {string} markdown - The markdown text to convert
 * @param {string} template - Template style to apply
 * @returns {Promise<Blob>} - A promise that resolves to a blob containing the Word document
 */
export async function convertMarkdownToWord(markdown, template = 'modern') {
  if (!markdown) return null;

  // Parse the markdown
  const lines = markdown.split('\n');
  
  // Create a new document
  const doc = new Document({
    creator: "CleverCV",
    title: "Professional CV",
    description: "A CV generated with CleverCV",
    styles: {
      paragraphStyles: [
        {
          id: "Heading1",
          name: "Heading 1",
          basedOn: "Normal",
          next: "Normal",
          quickFormat: true,
          run: {
            size: 36, // 18pt
            bold: true,
            color: template === 'modern' ? "5469d4" : 
                   template === 'creative' ? "7950f2" : "000000"
          },
          paragraph: {
            spacing: { after: 240 }, // 12pt spacing
          }
        },
        {
          id: "Heading2",
          name: "Heading 2",
          basedOn: "Normal",
          next: "Normal",
          quickFormat: true,
          run: {
            size: 32, // 16pt
            bold: true,
            color: template === 'modern' ? "5469d4" : 
                   template === 'creative' ? "7950f2" : "000000"
          },
          paragraph: {
            spacing: { before: 240, after: 120 } // 12pt before, 6pt after
          }
        },
        {
          id: "Normal",
          name: "Normal",
          run: {
            size: 24, // 12pt
          },
          paragraph: {
            spacing: { before: 120, after: 120 }, // 6pt spacing
          }
        }
      ]
    }
  });
  
  // Create section
  const section = doc.addSection({
    properties: {
      page: {
        margin: {
          top: 1440, // 1 inch
          right: 1440,
          bottom: 1440,
          left: 1440
        }
      }
    }
  });

  // Process markdown lines into Word document elements
  let inList = false;
  let listItems = [];
  let currentParagraph = null;
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    
    // Skip empty lines but end lists
    if (!line) {
      if (inList) {
        // Add the accumulated list
        addBulletList(section, listItems);
        listItems = [];
        inList = false;
      }
      continue;
    }
    
    // Process heading levels
    if (line.startsWith('# ')) {
      // End any list in progress
      if (inList) {
        addBulletList(section, listItems);
        listItems = [];
        inList = false;
      }
      
      // H1 heading
      section.addParagraph(
        new Paragraph({
          text: line.substring(2),
          heading: HeadingLevel.HEADING_1,
          alignment: template === 'classic' ? AlignmentType.CENTER : AlignmentType.LEFT,
          ...(template === 'modern' && { border: { bottom: { color: "5469d4", size: 3, style: BorderStyle.SINGLE } } }),
        })
      );
    } else if (line.startsWith('## ')) {
      // End any list in progress
      if (inList) {
        addBulletList(section, listItems);
        listItems = [];
        inList = false;
      }
      
      // H2 heading
      section.addParagraph(
        new Paragraph({
          text: line.substring(2),
          heading: HeadingLevel.HEADING_2,
          ...(template === 'modern' && { border: { bottom: { color: "5469d4", size: 1, style: BorderStyle.SINGLE } } }),
          ...(template === 'creative' && { indent: { left: 360 } }) // Creative style indent for H2
        })
      );
    } else if (line.startsWith('### ')) {
      // End any list in progress
      if (inList) {
        addBulletList(section, listItems);
        listItems = [];
        inList = false;
      }
      
      // H3 heading
      section.addParagraph(
        new Paragraph({
          text: line.substring(4),
          heading: HeadingLevel.HEADING_3
        })
      );
    } else if (line.startsWith('- ') || line.startsWith('* ')) {
      // List item
      inList = true;
      // Extract and format the list item text (handling basic formatting)
      const listText = processTextFormatting(line.substring(2));
      listItems.push(listText);
    } else {
      // End any list in progress
      if (inList) {
        addBulletList(section, listItems);
        listItems = [];
        inList = false;
      }
      
      // Regular paragraph with formatting
      const formattedText = processTextFormatting(line);
      section.addParagraph(formattedText);
    }
  }
  
  // Handle any remaining list items
  if (inList && listItems.length > 0) {
    addBulletList(section, listItems);
  }
  
  // Generate Word document as blob
  try {
    return await Packer.toBlob(doc);
  } catch (err) {
    console.error('Error generating Word document:', err);
    throw new Error(`Failed to generate Word document: ${err.message}`);
  }
}

/**
 * Processes text formatting for markdown elements like bold, italic, etc.
 * @param {string} text - The text to process
 * @returns {Paragraph} - A paragraph object with formatted text runs
 */
function processTextFormatting(text) {
  if (!text) return new Paragraph();
  
  const paragraph = new Paragraph();
  
  // Handle basic formatting: bold, italic, links
  
  // Split the text by formatting markers and track the formatting state
  let remainingText = text;
  let boldActive = false;
  let italicActive = false;
  let linkText = null;
  let linkUrl = null;
  
  // Simple parsing approach - more complex parsing would need a proper state machine
  let currentText = "";
  
  // Process simple bold and italic
  for (let i = 0; i < text.length; i++) {
    if (i < text.length - 1 && text.substr(i, 2) === '**' && !boldActive) {
      // Start bold
      if (currentText) paragraph.addRun(new TextRun(currentText));
      currentText = "";
      boldActive = true;
      i++; // Skip the second *
    } else if (i < text.length - 1 && text.substr(i, 2) === '**' && boldActive) {
      // End bold
      paragraph.addRun(new TextRun({ text: currentText, bold: true }));
      currentText = "";
      boldActive = false;
      i++; // Skip the second *
    } else if (text[i] === '*' && !italicActive && !boldActive) {
      // Start italic
      if (currentText) paragraph.addRun(new TextRun(currentText));
      currentText = "";
      italicActive = true;
    } else if (text[i] === '*' && italicActive) {
      // End italic
      paragraph.addRun(new TextRun({ text: currentText, italic: true }));
      currentText = "";
      italicActive = false;
    } else {
      currentText += text[i];
    }
  }
  
  // Add any remaining text
  if (currentText) {
    paragraph.addRun(new TextRun({ 
      text: currentText,
      bold: boldActive,
      italic: italicActive
    }));
  }
  
  // If no formatted elements were found, just add the whole text
  if (paragraph.children.length === 0) {
    paragraph.addRun(new TextRun(text));
  }
  
  return paragraph;
}

/**
 * Adds a bullet list to the document section
 * @param {Section} section - The document section to add to
 * @param {Array<string>} items - List items
 */
function addBulletList(section, items) {
  if (!items || !Array.isArray(items) || items.length === 0) return;
  
  for (const item of items) {
    if (!item) continue;
    
    const bulletParagraph = new Paragraph({
      bullet: {
        level: 0
      },
    });
    
    // Check if the item is a simple string or a paragraph with formatted text runs
    if (typeof item === 'string') {
      bulletParagraph.addRun(new TextRun(item));
    } else {
      // Copy the text runs from the processed paragraph
      if (item.children && Array.isArray(item.children)) {
        for (const run of item.children) {
          bulletParagraph.addRun(run);
        }
      } else {
        // Fallback if item is an object but doesn't have expected structure
        bulletParagraph.addRun(new TextRun(String(item)));
      }
    }
    
    section.addParagraph(bulletParagraph);
  }
}

export default convertMarkdownToWord; 